import React, { useState } from 'react'
import type { SetupClient } from '../../../api/setupClient'
import {
  StepContainer,
  StepHeading,
  StepDescription,
  ErrorAlert,
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldHelperText,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
} from '../setupStyled'

interface AdminAccountProps {
  client: SetupClient
  onNext: () => void
  onBack: () => void
}

/** Minimum acceptable length for the client-side policy hint — the server enforces the authoritative policy (PasswordPolicy, config-driven). */
const MIN_PASSWORD_LENGTH = 12

/**
 * Second step: create the bootstrap administrator account.
 *
 * The operator enters the admin email/password (and optionally a display
 * name) here, and this step submits them to the server via
 * `client.createAdmin(email, password, name)` — `POST /v1/init/admin` with
 * `{ email, password, name }` in the body, authenticated by the setup token
 * (see `setupClient.ts`). The server validates the password against its
 * runtime PasswordPolicy, hashes it (Argon2id), and creates a RoleAdmin user
 * (see init_server.go's `initCreateAdmin` / `createAdminFromBody`) — the
 * plaintext password is transmitted exactly once, over this call, and never
 * logged or audited.
 *
 * The length hint below (`MIN_PASSWORD_LENGTH`) is a client-side UX
 * convenience only, so the operator gets fast feedback before submitting —
 * the server's PasswordPolicy is authoritative and may reject a password
 * that passes this hint (e.g. composition-rule requirements); any such
 * rejection is surfaced as a server error below rather than blocked
 * client-side.
 *
 * OIDCPAM Gate-6 Phase 2b: migrated off MUI onto a minimal local themed
 * form (see setupStyled.ts) — the createAdmin submit/validation logic below
 * is unchanged from the pre-migration version.
 */
export const AdminAccount = ({
  client,
  onNext,
  onBack,
}: AdminAccountProps): JSX.Element => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  const policyOk = email.includes('@') && password.length >= MIN_PASSWORD_LENGTH

  const handleNext = async (): Promise<void> => {
    setStatus('working')
    setError(null)
    try {
      await client.createAdmin(email, password, name)
      setStatus('idle')
      onNext()
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : 'Failed to create the admin account'
      )
      setStatus('error')
    }
  }

  return (
    <StepContainer>
      <StepHeading>Admin account</StepHeading>
      <StepDescription>
        Create the bootstrap administrator account for this deployment.
      </StepDescription>
      {status === 'error' && error && <ErrorAlert role="alert">{error}</ErrorAlert>}
      <FieldGroup>
        <FieldLabel htmlFor="admin-email">Email</FieldLabel>
        <FieldInput
          id="admin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="off"
        />
      </FieldGroup>
      <FieldGroup>
        <FieldLabel htmlFor="admin-password">Password</FieldLabel>
        <FieldInput
          id="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <FieldHelperText>At least {MIN_PASSWORD_LENGTH} characters.</FieldHelperText>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel htmlFor="admin-name">Name (optional)</FieldLabel>
        <FieldInput
          id="admin-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
      </FieldGroup>
      <ButtonRow>
        <SecondaryButton
          type="button"
          onClick={onBack}
          disabled={status === 'working'}
        >
          Back
        </SecondaryButton>
        <PrimaryButton
          type="button"
          disabled={!policyOk || status === 'working'}
          onClick={() => handleNext()}
          aria-busy={status === 'working' || undefined}
        >
          {status === 'working' ? 'Working…' : 'Next'}
        </PrimaryButton>
      </ButtonRow>
    </StepContainer>
  )
}
